import React, { useState } from "react";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import { EQUIPMENT_LABEL } from "../lib/format";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const Field = ({ label, children, testid }) => (
  <div>
    <label className="label-base">{label}</label>
    {React.cloneElement(children, { "data-testid": testid })}
  </div>
);

export default function NewLoad() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [calc, setCalc] = useState({ busy: false, label: "", err: "" });
  const [f, setF] = useState({
    pickup_address: "", pickup_city: "", pickup_state: "TX", pickup_zip: "",
    dropoff_address: "", dropoff_city: "", dropoff_state: "CA", dropoff_zip: "",
    pickup_time: "", delivery_time: "",
    equipment_type: "van", weight: 20000, rate: 2000, distance: 1200,
    notes: "", status: "published",
  });

  const autoDistance = async () => {
    setCalc({ busy: true, label: "", err: "" });
    try {
      const r = await api.get("/tools/distance", { params: { from_zip: f.pickup_zip, to_zip: f.dropoff_zip } });
      setF((prev) => ({ ...prev, distance: r.data.road_miles }));
      setCalc({ busy: false, label: `${r.data.road_miles} road mi · ${r.data.eta_label}`, err: "" });
    } catch (e) {
      setCalc({ busy: false, label: "", err: e.response?.data?.detail || "Could not calculate" });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/loads", {
        ...f,
        weight: Number(f.weight), rate: Number(f.rate), distance: Number(f.distance),
        pickup_time: new Date(f.pickup_time || Date.now()).toISOString(),
        delivery_time: new Date(f.delivery_time || Date.now() + 2*86400000).toISOString(),
      });
      navigate(`/app/loads/${r.data.load_id}`);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to create load");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl">
      <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">Intake</div>
      <h1 className="font-display text-4xl font-bold tracking-tighter text-navy mb-2">Post a load</h1>
      <p className="text-slate-500 mb-8">Tell carriers what you need to move.</p>

      <form onSubmit={submit} className="card-base p-6 md:p-8 space-y-8" data-testid="new-load-form">
        <section>
          <div className="font-display text-lg font-bold text-navy mb-4">Pickup</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Address" testid="pickup-address"><input className="input-base" value={f.pickup_address} onChange={e => setF({...f, pickup_address: e.target.value})} required /></Field>
            <Field label="City" testid="pickup-city"><input className="input-base" value={f.pickup_city} onChange={e => setF({...f, pickup_city: e.target.value})} required /></Field>
            <Field label="State" testid="pickup-state"><select className="input-base" value={f.pickup_state} onChange={e => setF({...f, pickup_state: e.target.value})}>{STATES.map(s => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Zip" testid="pickup-zip"><input className="input-base" value={f.pickup_zip} onChange={e => setF({...f, pickup_zip: e.target.value})} required /></Field>
            <Field label="Pickup time" testid="pickup-time"><input type="datetime-local" className="input-base" value={f.pickup_time} onChange={e => setF({...f, pickup_time: e.target.value})} required /></Field>
          </div>
        </section>

        <section>
          <div className="font-display text-lg font-bold text-navy mb-4">Dropoff</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Address" testid="dropoff-address"><input className="input-base" value={f.dropoff_address} onChange={e => setF({...f, dropoff_address: e.target.value})} required /></Field>
            <Field label="City" testid="dropoff-city"><input className="input-base" value={f.dropoff_city} onChange={e => setF({...f, dropoff_city: e.target.value})} required /></Field>
            <Field label="State" testid="dropoff-state"><select className="input-base" value={f.dropoff_state} onChange={e => setF({...f, dropoff_state: e.target.value})}>{STATES.map(s => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Zip" testid="dropoff-zip"><input className="input-base" value={f.dropoff_zip} onChange={e => setF({...f, dropoff_zip: e.target.value})} required /></Field>
            <Field label="Delivery time" testid="delivery-time"><input type="datetime-local" className="input-base" value={f.delivery_time} onChange={e => setF({...f, delivery_time: e.target.value})} required /></Field>
          </div>
        </section>

        <section>
          <div className="font-display text-lg font-bold text-navy mb-4">Freight</div>
          <div className="grid md:grid-cols-4 gap-4">
            <Field label="Equipment" testid="equipment"><select className="input-base" value={f.equipment_type} onChange={e => setF({...f, equipment_type: e.target.value})}>{Object.entries(EQUIPMENT_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
            <Field label="Weight (lbs)" testid="weight"><input type="number" className="input-base" value={f.weight} onChange={e => setF({...f, weight: e.target.value})} required /></Field>
            <Field label="Distance (mi)" testid="distance"><input type="number" className="input-base" value={f.distance} onChange={e => setF({...f, distance: e.target.value})} required /></Field>
            <div className="flex flex-col justify-end">
              <button type="button" onClick={autoDistance} disabled={calc.busy || !f.pickup_zip || !f.dropoff_zip}
                className="btn-outline w-full" data-testid="auto-distance-btn" title="Estimate road miles from pickup & dropoff ZIPs">
                {calc.busy ? "Calculating…" : "Auto-calc from ZIPs"}
              </button>
              {calc.label && <div className="text-xs text-navy font-semibold mt-1" data-testid="distance-estimate">{calc.label}</div>}
              {calc.err && <div className="text-xs text-red-600 mt-1" data-testid="distance-error">{calc.err}</div>}
            </div>
            <Field label="Rate (USD)" testid="rate"><input type="number" step="0.01" className="input-base" value={f.rate} onChange={e => setF({...f, rate: e.target.value})} required /></Field>
            <Field label="Status" testid="status">
              <select className="input-base" value={f.status} onChange={e => setF({...f, status: e.target.value})}>
                <option value="draft">Draft</option>
                <option value="published">Publish to board</option>
              </select>
            </Field>
            <div className="md:col-span-3">
              <label className="label-base">Notes</label>
              <textarea className="input-base h-24" value={f.notes} onChange={e => setF({...f, notes: e.target.value})} data-testid="notes" />
            </div>
          </div>
        </section>

        {err && <div className="text-red-600 text-sm" data-testid="form-error">{err}</div>}

        <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="submit-load">
            {busy ? "Posting…" : f.status === "draft" ? "Save draft" : "Publish load"}
          </button>
          <button type="button" onClick={() => navigate("/app/loads")} className="btn-outline" data-testid="cancel-load">Cancel</button>
        </div>
      </form>
    </div>
  );
}
